/**
 * Pronunciation WebSocket Client
 * 
 * Dedicated WebSocket connection for pronunciation assessment.
 * Separate from VoiceLive conversation WebSocket for reliable audio capture.
 * 
 * Based on pronunciation-assessment-poc architecture.
 * 
 * Features:
 * - Binary PCM16 audio streaming (16kHz)
 * - Real-time word-by-word pronunciation hints
 * - Final assessment with complete metrics
 * - Independent from VoiceLive conversation
 * 
 * @author AI Assistant
 * @date 2025-11-23
 */

import { getWebSocketUrl } from '../utils/apiUtils';

export interface PronunciationHint {
  type: 'realtime_hint';
  word: string;
  accuracy: number;
  ipa: string;
  start_ms: number;
  duration_ms: number;
  word_sequence_number: number;  // Position of this word in the story (0-based)
}

export interface CurrentWordUpdate {
  type: 'current_word_update';
  current_word_index: number;
  partial_text: string;
}

export interface AssessmentComplete {
  type: 'assessment_complete';
  success: boolean;
  accuracy: number;
  fluency: number;
  completeness: number;
  prosody: number;
  pronunciation_score: number;
  words: Array<{
    word: string;
    accuracy: number;
    error_type: string;
    ipa: string;
    phonemes: Array<{
      phoneme: string;
      accuracy: number;
    }>;
  }>;
  transcript: string;
  word_count: number;
}

export interface PronunciationError {
  type: 'error';
  message: string;
}

export type PronunciationMessage = PronunciationHint | CurrentWordUpdate | AssessmentComplete | PronunciationError;

export type PronunciationEventCallback = (message: PronunciationMessage) => void;

// Event map for typed event listeners
export interface PronunciationEvents {
  connected: () => void;
  realtime_hint: (data: PronunciationHint) => void;
  current_word_update: (data: CurrentWordUpdate) => void;
  assessment_complete: (result: AssessmentComplete) => void;
  error: (error: PronunciationError) => void;
}

export class PronunciationWebSocketClient {
  private ws: WebSocket | null = null;
  private conversationId: string;
  private eventListeners: Map<keyof PronunciationEvents, Set<Function>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectDelay = 2000;
  private isIntentionallyClosed = false;

  constructor(conversationId: string) {
    this.conversationId = conversationId;
  }

  /**
   * Connect to the pronunciation WebSocket endpoint
   */
  async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Build WebSocket URL using proper WebSocket protocol
        const wsUrl = getWebSocketUrl(`ws-pronunciation/${this.conversationId}`);

        console.log(`[PronunciationWS] Connecting to: ${wsUrl}`);

        this.ws = new WebSocket(wsUrl);
        this.ws.binaryType = 'arraybuffer';

        this.ws.onopen = () => {
          console.log(`[PronunciationWS] ✅ Connected: ${this.conversationId}`);
          this.reconnectAttempts = 0;
          this.emit('connected');
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: PronunciationMessage = JSON.parse(event.data);
            console.log(`[PronunciationWS] 📨 Message received:`, message);
            
            // Emit typed events based on message type
            if (message.type === 'realtime_hint') {
              this.emit('realtime_hint', message);
            } else if (message.type === 'current_word_update') {
              this.emit('current_word_update', message);
            } else if (message.type === 'assessment_complete') {
              this.emit('assessment_complete', message);
            } else if (message.type === 'error') {
              this.emit('error', message);
            }
          } catch (error) {
            console.error('[PronunciationWS] Failed to parse message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error(`[PronunciationWS] ❌ Error:`, error);
          const errorMessage: PronunciationError = {
            type: 'error',
            message: 'WebSocket connection error'
          };
          this.emit('error', errorMessage);
          reject(error);
        };

        this.ws.onclose = (event) => {
          console.log(`[PronunciationWS] 🔌 Disconnected: ${event.code} ${event.reason}`);
          
          // Attempt reconnection if not intentionally closed
          if (!this.isIntentionallyClosed && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            console.log(`[PronunciationWS] 🔄 Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
            
            setTimeout(() => {
              this.connect().catch(error => {
                console.error('[PronunciationWS] Reconnection failed:', error);
              });
            }, this.reconnectDelay);
          }
        };

      } catch (error) {
        console.error('[PronunciationWS] Connection error:', error);
        reject(error);
      }
    });
  }

  /**
   * Set the reference text (story) for pronunciation assessment
   */
  setStory(referenceText: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('[PronunciationWS] Cannot set story: WebSocket not connected');
      return;
    }

    const message = {
      type: 'set_story',
      reference_text: referenceText
    };

    console.log(`[PronunciationWS] 📖 Setting story: ${referenceText.length} chars`);
    this.ws.send(JSON.stringify(message));
  }

  /**
   * Start pronunciation assessment (begin continuous recognition)
   * @param startWordIndex - Optional starting word position in story (for continuing after persona conversation)
   */
  startAssessment(startWordIndex: number = 0): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('[PronunciationWS] Cannot start assessment: WebSocket not connected');
      return;
    }

    const message = {
      type: 'start_assessment',
      start_word_index: startWordIndex
    };

    console.log(`[PronunciationWS] ▶️ Starting assessment from word ${startWordIndex}`);
    this.ws.send(JSON.stringify(message));
  }

  /**
   * End pronunciation assessment (stop recognition and get final report)
   */
  endAssessment(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.error('[PronunciationWS] Cannot end assessment: WebSocket not connected');
      return;
    }

    const message = {
      type: 'end_assessment'
    };

    console.log('[PronunciationWS] ⏹️ Ending assessment');
    this.ws.send(JSON.stringify(message));
  }

  /**
   * Send binary audio frame (PCM16, 16kHz, mono)
   */
  sendAudioFrame(audioData: ArrayBuffer): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[PronunciationWS] Cannot send audio: WebSocket not connected');
      return;
    }

    // Send raw binary data
    this.ws.send(audioData);
  }

  /**
   * Register typed event listener
   */
  on<K extends keyof PronunciationEvents>(
    event: K,
    callback: PronunciationEvents[K]
  ): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback);
  }

  /**
   * Unregister event listener
   */
  off<K extends keyof PronunciationEvents>(
    event: K,
    callback: PronunciationEvents[K]
  ): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
  }

  /**
   * Emit event to all registered listeners
   */
  private emit<K extends keyof PronunciationEvents>(
    event: K,
    ...args: Parameters<PronunciationEvents[K]>
  ): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          (listener as Function)(...args);
        } catch (error) {
          console.error(`[PronunciationWS] Event listener error (${event}):`, error);
        }
      });
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    if (this.ws) {
      this.isIntentionallyClosed = true;
      console.log('[PronunciationWS] 🔌 Disconnecting...');
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Get conversation ID
   */
  getConversationId(): string {
    return this.conversationId;
  }
}
